// ⚠️ SPIKE DESECHABLE — página de calibración para la proyección 2027 de Medifarma Perú.
// Modelo 100% client-side con base en data real (Ago–Oct 2026). Sin backend, sin tests.
// Una vez calibrados los parámetros con los sliders, se fijan y se hace la versión final.
import { useState, useMemo } from 'react'
import { FileText, TrendingUp, Users, MessageCircle, Coins, SlidersHorizontal, ChevronDown, ChevronUp } from 'lucide-react'
import {
  Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Line, ComposedChart, ReferenceLine, ResponsiveContainer, LabelList,
} from 'recharts'
import { Slider } from '@/components/ui/slider'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'

// ── Constantes de color ───────────────────────────────────────────────────────
const PURPLE = '#6a5cbc'
const PURPLE_LIGHT = '#6a5cbc18'
const GREEN = '#2fbc80'

// ── Base real (medido en prod, schema medifarmaperu, Ago–Oct 2026) ─────────────
// Promedio ponderado por día de los 3 meses reales (diluye el pico de promos de set.).
const PRODUCTOS_BASE = 20          // catálogo actual
const DEPS_BASE = 2000             // participantes registrados ~fin 2026
const BASE_FACT_POR_PRODUCTO = 730 // facturas/producto/mes @ 20 productos
const BASE_PUNTOS_POR_PRODUCTO = 575
const FACT_BASE_MES = BASE_FACT_POR_PRODUCTO * PRODUCTOS_BASE     // 14.600
const PUNTOS_BASE_MES = BASE_PUNTOS_POR_PRODUCTO * PRODUCTOS_BASE // 11.500

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

// ── Helpers ─────────────────────────────────────────────────────────────────
function fmt(n: number): string {
  return Math.round(n).toLocaleString('es-PE')
}
const num = (x: number | '') => (typeof x === 'number' ? x : 0)

// ── Modelo de proyección ───────────────────────────────────────────────────────
interface MesProj {
  mes: string
  productos: number
  deps_acum: number
  registros_nuevos: number
  facturas: number
  facturas_acum: number
  puntos: number
  canje: number
  envios: number
  costo_envios: number
  ventas: number
  costo_facturas: number
  skus_nuevos: number
  costo_skus: number
}

interface Params {
  efectoProductos: number   // 0–100 → exponente aProd (100 = lineal, menos = decreciente)
  efectoDependientes: number // 0–100 → exponente aDeps
  tasaCanje: number         // %
  registrosMes: number      // nuevos dependientes por mes
  cat1: number; cat2: number; cat3: number // tamaño del catálogo por tramo
  campTotal: number         // campañas al 100% de la base / mes
  campSeg: number           // campañas segmentadas / mes
  pctSeg: number            // % de la base al que llega cada campaña segmentada
  costoEnvio: number        // S/ por envío
  costoFactura: number      // S/ por factura procesada
  valorPorFactura: number   // S/ promedio de productos participantes por factura (real)
  pvpActual: number         // PVP promedio catálogo actual (ref)
  pvpNuevos: number         // PVP promedio de los productos nuevos (lo pasa el cliente; 0 = sin dato)
  nuevosSkus: number        // SKUs nuevos a ingresar (total año)
  costoSku: number          // S/ por SKU ingresado (cargo único)
}

function proyectar(p: Params): MesProj[] {
  const aProd = p.efectoProductos / 100
  const aDeps = p.efectoDependientes / 100
  // Ajuste del valor por factura según el PVP del catálogo nuevo (regla del cliente):
  // valor_nuevo = valor_actual × (PVP_blend / PVP_actual), blend = promedio(PVP_actual, PVP_nuevos).
  const valorFactura = p.pvpNuevos > 0 && p.pvpActual > 0
    ? p.valorPorFactura * ((p.pvpActual + p.pvpNuevos) / 2) / p.pvpActual
    : p.valorPorFactura
  // Ingreso de SKUs: se distribuye en los meses donde entra cada tanda del catálogo,
  // escalado para que el total del año = nuevosSkus (alineado con el filtro de meses).
  const crecimientoCatalogo = p.cat3 - PRODUCTOS_BASE
  let prevProd = PRODUCTOS_BASE
  const out: MesProj[] = []
  let facturasAcum = 0
  for (let m = 1; m <= 12; m++) {
    const productos = m <= 3 ? p.cat1 : m <= 6 ? p.cat2 : p.cat3
    const incrementoProd = Math.max(0, productos - prevProd)
    prevProd = productos
    const skusNuevos = crecimientoCatalogo > 0 ? (incrementoProd / crecimientoCatalogo) * p.nuevosSkus : 0
    const costoSkus = skusNuevos * p.costoSku
    const depsAcum = DEPS_BASE + p.registrosMes * m
    // Dos drivers con rendimientos decrecientes (power-law). En base (20 prod, 2000 deps) ambos = 1.
    const factorProd = Math.pow(productos / PRODUCTOS_BASE, aProd)
    const factorDeps = Math.pow(depsAcum / DEPS_BASE, aDeps)
    const facturas = FACT_BASE_MES * factorProd * factorDeps
    const puntos = PUNTOS_BASE_MES * factorProd * factorDeps
    const canje = puntos * (p.tasaCanje / 100)
    facturasAcum += facturas
    // WhatsApp: cada mes se manda a la base acumulada. campañas al 100% + segmentadas al pctSeg%.
    const enviosPorContacto = p.campTotal + p.campSeg * (p.pctSeg / 100)
    const envios = depsAcum * enviosPorContacto
    const costoEnvios = envios * p.costoEnvio
    const ventas = facturas * valorFactura
    const costoFacturas = facturas * p.costoFactura
    out.push({
      mes: MESES[m - 1],
      productos,
      deps_acum: depsAcum,
      registros_nuevos: p.registrosMes,
      facturas,
      facturas_acum: facturasAcum,
      puntos,
      canje,
      envios,
      costo_envios: costoEnvios,
      ventas,
      costo_facturas: costoFacturas,
      skus_nuevos: skusNuevos,
      costo_skus: costoSkus,
    })
  }
  return out
}

// ── KPI Card (con mini-KPI opcional) ───────────────────────────────────────────
function KpiCard({ label, value, sub, mini, icon: Icon }: {
  label: string; value: string; sub?: string; mini?: { label: string; value: string }; icon: React.ElementType
}) {
  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-card h-full"
      style={{ backgroundImage: `linear-gradient(to right, ${PURPLE_LIGHT}, transparent 60%)` }}
    >
      <div className="h-full px-4 py-4 flex flex-col justify-center gap-2">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <div className="flex items-center gap-3">
          <div className="shrink-0"><Icon className="h-9 w-9" style={{ color: PURPLE }} /></div>
          <div className="flex-1 min-w-0 text-right">
            <div className="text-2xl font-bold text-foreground leading-tight">{value}</div>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
        {mini && (
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{mini.label}</span>
            <span className="text-sm font-bold" style={{ color: PURPLE }}>{mini.value}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Slider de parámetro ─────────────────────────────────────────────────────────
function SliderInput({ label, value, min, max, step, onChange, suffix, help }: {
  label: string; value: number | ''; min: number; max: number; step: number
  onChange: (v: number | '') => void; suffix?: string; help?: string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <div className="flex items-center gap-1">
          <input
            type="number" min={min} max={max} step={step} value={value}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-20 text-right text-lg font-bold bg-transparent border-b border-transparent hover:border-gray-300 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            style={{ color: PURPLE }}
          />
          {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
        </div>
      </div>
      <Slider
        value={[num(value)]} min={min} max={max} step={step}
        onValueChange={(v) => onChange(v[0])}
        className="[&>span:first-child]:bg-purple-500/20 [&>span:first-child>span]:bg-[#6a5cbc] [&_[role=slider]]:border-[#6a5cbc]"
      />
      {help && <p className="text-[11px] text-muted-foreground mt-1.5">{help}</p>}
    </div>
  )
}

// ── Tooltip custom ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label, soles }: {
  active?: boolean; payload?: { value: number; name: string; color: string }[]; label?: string; soles?: boolean
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-card border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-semibold text-foreground mb-1">{label} 2027</p>
      {payload.map((p) => (
        <p key={p.name} className="text-muted-foreground" style={{ color: p.color }}>
          {p.name}: <span className="font-medium">{soles ? 'S/ ' : ''}{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  )
}

// Tooltip de facturas: muestra facturas + su costo de procesamiento en soles
function FacturasTooltip({ active, payload, label, costoFactura }: {
  active?: boolean; payload?: { value: number; name: string; color: string; payload: MesProj }[]
  label?: string; costoFactura: number
}) {
  if (!active || !payload?.length) return null
  const punto = payload[0].payload
  return (
    <div className="bg-white dark:bg-card border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-semibold text-foreground mb-1">{label} 2027</p>
      <p className="text-muted-foreground" style={{ color: PURPLE }}>Facturas procesadas: <span className="font-medium">{fmt(punto.facturas)}</span></p>
      <p className="text-muted-foreground">Ventas generadas: <span className="font-medium">S/ {fmt(punto.ventas)}</span></p>
      <p className="text-muted-foreground">Costo proceso: <span className="font-medium">S/ {fmt(punto.facturas * costoFactura)}</span></p>
      <p className="text-muted-foreground">Facturas procesadas / ventas: <span className="font-medium">{punto.ventas > 0 ? (100 * punto.facturas * costoFactura / punto.ventas).toFixed(2) : '0'}%</span></p>
      <p className="text-muted-foreground" style={{ color: GREEN }}>Acumulado facturas: <span className="font-medium">{fmt(punto.facturas_acum)}</span></p>
    </div>
  )
}

// ── Página principal ─────────────────────────────────────────────────────────
export function MedifarmaPeru2027Page() {
  useDocumentTitle('Proyección 2027 · Medifarma Perú')

  const [showCalib, setShowCalib] = useState(false)
  const [efectoProductos, setEfectoProductos] = useState<number | ''>(50)
  const [efectoDependientes, setEfectoDependientes] = useState<number | ''>(50)
  const [tasaCanje, setTasaCanje] = useState<number | ''>(40)
  const [registrosMes, setRegistrosMes] = useState<number | ''>(600)
  const [cat1, setCat1] = useState<number | ''>(100)
  const [cat2, setCat2] = useState<number | ''>(100)
  const [cat3, setCat3] = useState<number | ''>(100)
  const [campTotal, setCampTotal] = useState<number | ''>(2)
  const [campSeg, setCampSeg] = useState<number | ''>(2)
  const [pctSeg, setPctSeg] = useState<number | ''>(33)
  const [costoEnvio, setCostoEnvio] = useState<number | ''>(0.42)
  const [costoFactura, setCostoFactura] = useState<number | ''>(0.40)
  const [valorPorFactura, setValorPorFactura] = useState<number | ''>(10)
  const [pvpActual, setPvpActual] = useState<number | ''>(34.86)
  const [pvpNuevos, setPvpNuevos] = useState<number | ''>(0)
  const [nuevosSkus, setNuevosSkus] = useState<number | ''>(100)
  const [costoSku, setCostoSku] = useState<number | ''>(245)

  const serie = useMemo(() => proyectar({
    efectoProductos: num(efectoProductos),
    efectoDependientes: num(efectoDependientes),
    tasaCanje: num(tasaCanje),
    registrosMes: num(registrosMes),
    cat1: num(cat1), cat2: num(cat2), cat3: num(cat3),
    campTotal: num(campTotal), campSeg: num(campSeg), pctSeg: num(pctSeg), costoEnvio: num(costoEnvio),
    costoFactura: num(costoFactura), valorPorFactura: num(valorPorFactura), pvpActual: num(pvpActual), pvpNuevos: num(pvpNuevos),
    nuevosSkus: num(nuevosSkus), costoSku: num(costoSku),
  }), [efectoProductos, efectoDependientes, tasaCanje, registrosMes, cat1, cat2, cat3, campTotal, campSeg, pctSeg, costoEnvio, costoFactura, valorPorFactura, pvpActual, pvpNuevos, nuevosSkus, costoSku])

  // Filtro de meses (multi-selección). Las gráficas y totales reflejan los meses elegidos.
  const [mesesSel, setMesesSel] = useState<boolean[]>(() => Array(12).fill(true))
  const toggleMes = (i: number) => setMesesSel((prev) => prev.map((v, idx) => (idx === i ? !v : v)))
  const todosMeses = mesesSel.every(Boolean)
  const serieVista = serie.filter((_, i) => mesesSel[i])
  const vista = serieVista.length ? serieVista : serie // fallback si no hay nada seleccionado

  const totFacturas = vista.reduce((a, m) => a + m.facturas, 0)
  const totPuntos = vista.reduce((a, m) => a + m.puntos, 0)
  const totCanje = vista.reduce((a, m) => a + m.canje, 0)
  const totEnvios = vista.reduce((a, m) => a + m.envios, 0)
  const totCostoEnvios = vista.reduce((a, m) => a + m.costo_envios, 0)
  const totCostoFacturas = vista.reduce((a, m) => a + m.costo_facturas, 0)
  const totVentas = vista.reduce((a, m) => a + m.ventas, 0)
  const totCostoSkus = vista.reduce((a, m) => a + m.costo_skus, 0) // alineado al filtro de meses
  const totCostos = totCanje + totCostoFacturas + totCostoEnvios + totCostoSkus
  const pct = (x: number) => (totVentas > 0 ? (100 * x / totVentas) : 0)
  // ROI: inversión = total de costos del programa
  const inversionTotal = totCostos
  const retornoNeto = totVentas - inversionTotal
  const roi = inversionTotal > 0 ? (100 * retornoNeto / inversionTotal) : 0
  const depsFin = vista[vista.length - 1].deps_acum
  const prodFin = vista[vista.length - 1].productos
  const mesesSku = vista.filter((m) => m.skus_nuevos > 0).map((m) => m.mes) // meses donde entran SKUs

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      {/* ── Header ── */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="py-4 px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-12 w-auto dark:invert" />
              <div className="border-l border-gray-200 dark:border-gray-700 pl-5">
                <h1 className="text-xl font-bold text-foreground leading-tight">
                  Proyección 2027 — Programa de Fidelización
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Medifarma Perú · basada en data real Ago–Oct 2026
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowCalib((v) => !v)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {showCalib ? 'Ocultar calibración' : 'Calibrar'}
              {showCalib ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* ── Panel de calibración (oculto por defecto) ── */}
      {showCalib && (
        <Card className="border border-gray-200 dark:border-gray-700" style={{ backgroundImage: `linear-gradient(to right, ${PURPLE_LIGHT}, transparent 80%)` }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Calibración del modelo (spike)
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SliderInput
              label="Efecto productos" value={efectoProductos} min={0} max={100} step={5}
              onChange={setEfectoProductos} suffix="%"
              help="100% = cada producto nuevo suma lo mismo (lineal). Menos = rendimiento decreciente."
            />
            <SliderInput
              label="Efecto dependientes" value={efectoDependientes} min={0} max={100} step={5}
              onChange={setEfectoDependientes} suffix="%"
              help="100% = lineal (doble deps ⇒ doble facturas). Menos = amortiguado."
            />
            <SliderInput
              label="Tasa de canje" value={tasaCanje} min={0} max={100} step={1}
              onChange={setTasaCanje} suffix="%"
              help="% de los puntos otorgados que se canjean (1 punto = S/1). Real Medifarma Perú: 36% (8.590/23.831). Benchmark otros programas: 55%."
            />
            <SliderInput
              label="Registros nuevos / mes" value={registrosMes} min={0} max={3000} step={50}
              onChange={setRegistrosMes} help={`Base ~${fmt(DEPS_BASE)} ⇒ fin de 2027 ≈ ${fmt(DEPS_BASE + num(registrosMes) * 12)}`}
            />
            <SliderInput label="Catálogo Ene–Mar" value={cat1} min={20} max={351} step={1} onChange={setCat1} suffix="prod." />
            <SliderInput label="Catálogo Abr–Jun" value={cat2} min={20} max={351} step={1} onChange={setCat2} suffix="prod." />
            <SliderInput label="Catálogo Jul–Dic" value={cat3} min={20} max={351} step={1} onChange={setCat3} suffix="prod." />
            <SliderInput label="Campañas al 100% base / mes" value={campTotal} min={0} max={30} step={1} onChange={setCampTotal} help="Actual: 5 campañas a toda la base." />
            <SliderInput label="Campañas segmentadas / mes" value={campSeg} min={0} max={30} step={1} onChange={setCampSeg} help="Actual: 4 campañas segmentadas." />
            <SliderInput label="Alcance de segmentadas" value={pctSeg} min={0} max={100} step={1} onChange={setPctSeg} suffix="%" help="Actual: ~33% de la base por campaña." />
            <SliderInput label="Costo por factura procesada" value={costoFactura} min={0} max={5} step={0.05} onChange={setCostoFactura} suffix="S/" help="Costo de procesamiento/OCR por factura. Actual: S/0.40." />
            <SliderInput label="Valor participante / factura" value={valorPorFactura} min={0} max={100} step={0.5} onChange={setValorPorFactura} suffix="S/" help="Venta real por factura (solo 10 productos CONSUMO). Real: S/14.25 (7.053 facturas)." />
            <SliderInput label="PVP promedio catálogo actual" value={pvpActual} min={1} max={200} step={1} onChange={setPvpActual} suffix="S/" help="Referencia de catálogo actual. Real: S/34.86/unidad." />
            <SliderInput label="PVP promedio productos nuevos" value={pvpNuevos} min={0} max={200} step={1} onChange={setPvpNuevos} suffix="S/" help="Lo pasa el cliente. 0 = sin dato (ventas sin ajuste de PVP)." />
            <SliderInput label="SKUs nuevos a ingresar" value={nuevosSkus} min={0} max={500} step={1} onChange={setNuevosSkus} help="Productos nuevos que se cargan al sistema. Actual: 321." />
            <SliderInput label="Costo por SKU ingresado" value={costoSku} min={0} max={1000} step={10} onChange={setCostoSku} suffix="S/" help="Cargo único por ingreso de cada SKU. Actual: S/250." />
            <div className="lg:col-span-3 text-xs text-muted-foreground/70 italic border-t border-gray-200 dark:border-gray-700 pt-3">
              Base real: {fmt(FACT_BASE_MES)} facturas/mes y {fmt(PUNTOS_BASE_MES)} puntos/mes con {PRODUCTOS_BASE} productos y ~{fmt(DEPS_BASE)} dependientes.
              Facturas(mes) = {fmt(FACT_BASE_MES)} × (prod/20)^{(num(efectoProductos) / 100).toFixed(2)} × (deps/2000)^{(num(efectoDependientes) / 100).toFixed(2)}.
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Filtro de meses (multi-selección) ── */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-card px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mr-1">Meses</span>
          {MESES.map((m, i) => {
            const on = mesesSel[i]
            return (
              <button
                key={m}
                type="button"
                onClick={() => toggleMes(i)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${on ? 'text-white border-transparent' : 'text-muted-foreground border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                style={on ? { backgroundColor: PURPLE } : undefined}
              >
                {m}
              </button>
            )
          })}
          <button
            type="button"
            onClick={() => setMesesSel(Array(12).fill(!todosMeses))}
            className="ml-auto px-2.5 py-1 rounded-md text-xs font-medium border border-gray-200 dark:border-gray-700 text-muted-foreground hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {todosMeses ? 'Limpiar' : 'Todos'}
          </button>
        </div>
        {serieVista.length === 0 && (
          <p className="text-[11px] text-muted-foreground/70 mt-2">Sin meses seleccionados — mostrando el año completo.</p>
        )}
      </div>

      {/* ── KPIs anuales (con mini-KPI de costo dentro de cada card) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          icon={Coins} label="Ventas 2027" value={`S/ ${fmt(totVentas)}`} sub="productos participantes"
          mini={{ label: 'Total costos', value: `${pct(totCostos).toFixed(1)}% ventas` }}
        />
        <KpiCard
          icon={FileText} label="Facturas 2027" value={fmt(totFacturas)} sub="total año"
          mini={{ label: `Costo proceso · S/${costoFactura}`, value: `S/ ${fmt(totCostoFacturas)}` }}
        />
        <KpiCard
          icon={TrendingUp} label="Puntos 2027" value={fmt(totPuntos)} sub="otorgados"
          mini={{ label: `Costo canjes · ${tasaCanje}%`, value: `S/ ${fmt(totCanje)}` }}
        />
        <KpiCard
          icon={Users} label="Dependientes fin 2027" value={fmt(depsFin)} sub={`+${fmt(num(registrosMes))}/mes`}
          mini={{ label: `Productos · desde ${PRODUCTOS_BASE}`, value: fmt(prodFin) }}
        />
        <KpiCard
          icon={MessageCircle} label="Envíos WhatsApp 2027" value={fmt(totEnvios)} sub={`${num(campTotal) + num(campSeg)} campañas/mes`}
          mini={{ label: `Costo · S/${costoEnvio}/envío`, value: `S/ ${fmt(totCostoEnvios)}` }}
        />
      </div>

      {/* ── Estructura de costos — fila de cards (segunda línea) ── */}
      <div>
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Estructura de costos 2027 — % sobre ventas
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Ventas = 100% */}
          <div className="rounded-lg border-2 border-gray-300 dark:border-gray-600 px-4 py-4 flex flex-col justify-between" style={{ backgroundImage: `linear-gradient(135deg, ${PURPLE_LIGHT}, transparent 70%)` }}>
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Ventas generadas</p>
            <div className="mt-2">
              <div className="text-xl font-bold text-foreground">S/ {fmt(totVentas)}</div>
              <div className="text-sm font-semibold" style={{ color: PURPLE }}>100%</div>
            </div>
          </div>
          {/* Un card por costo */}
          {[
            { label: 'Costo de canjes', val: totCanje },
            { label: 'Facturas procesadas', val: totCostoFacturas },
            { label: 'Campañas WhatsApp', val: totCostoEnvios },
            { label: 'Ingreso de SKUs', val: totCostoSkus },
          ].map((c) => (
            <div key={c.label} className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-4 flex flex-col justify-between bg-white dark:bg-card">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{c.label}</p>
              <div className="mt-2">
                <div className="text-xl font-bold text-foreground">S/ {fmt(c.val)}</div>
                <div className="text-sm font-semibold" style={{ color: PURPLE }}>{pct(c.val).toFixed(2)}%</div>
              </div>
            </div>
          ))}
          {/* Total % al final */}
          <div className="rounded-lg border-2 px-4 py-4 flex flex-col justify-between" style={{ borderColor: PURPLE, backgroundImage: `linear-gradient(135deg, ${PURPLE_LIGHT}, transparent 50%)` }}>
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Total costos</p>
            <div className="mt-2">
              <div className="text-2xl font-bold" style={{ color: PURPLE }}>{pct(totCostos).toFixed(2)}%</div>
              <div className="text-xs text-muted-foreground">S/ {fmt(totCostos)} · margen {(100 - pct(totCostos)).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── ROI ── */}
      <div>
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Retorno de inversión 2027{!todosMeses && ' · meses seleccionados'}
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-4 bg-white dark:bg-card">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Inversión total</p>
            <div className="text-xl font-bold text-foreground mt-2">S/ {fmt(inversionTotal)}</div>
            <p className="text-xs text-muted-foreground mt-0.5">suma de todos los costos</p>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-4 bg-white dark:bg-card">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Ventas generadas</p>
            <div className="text-xl font-bold text-foreground mt-2">S/ {fmt(totVentas)}</div>
            <p className="text-xs text-muted-foreground mt-0.5">productos participantes</p>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-4 bg-white dark:bg-card">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Retorno neto</p>
            <div className="text-xl font-bold mt-2" style={{ color: retornoNeto >= 0 ? GREEN : '#eb6587' }}>S/ {fmt(retornoNeto)}</div>
            <p className="text-xs text-muted-foreground mt-0.5">ventas − inversión</p>
          </div>
          <div className="rounded-lg border-2 px-4 py-4" style={{ borderColor: PURPLE, backgroundImage: `linear-gradient(135deg, ${PURPLE_LIGHT}, transparent 50%)` }}>
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">ROI</p>
            <div className="text-2xl font-bold mt-2" style={{ color: roi >= 0 ? GREEN : '#eb6587' }}>{roi.toFixed(0)}%</div>
            <p className="text-xs text-muted-foreground mt-0.5">retorno neto / inversión</p>
          </div>
        </div>
      </div>

      {/* ── Facturas mes a mes ── */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Facturas procesadas — mes a mes 2027
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={vista} margin={{ top: 16, right: 24, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="bar" tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => fmt(v)} />
              <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => fmt(v)} />
              <Tooltip content={<FacturasTooltip costoFactura={num(costoFactura)} />} />
              {mesesSku.map((mes) => (
                <ReferenceLine
                  key={mes}
                  yAxisId="bar"
                  x={mes}
                  stroke="#eb6587"
                  strokeDasharray="4 4"
                  label={{ value: 'SKUs', position: 'top', fontSize: 10, fill: '#eb6587' }}
                />
              ))}
              <Bar yAxisId="bar" dataKey="facturas" name="Facturas" fill={PURPLE} radius={[4, 4, 0, 0]} opacity={0.85}>
                <LabelList dataKey="facturas" position="top" formatter={(v: number) => fmt(v)} style={{ fontSize: 10, fill: '#6b7280' }} />
              </Bar>
              <Line yAxisId="line" dataKey="facturas_acum" name="Acumulado" stroke={GREEN} strokeWidth={2} dot={{ r: 3, fill: GREEN }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* ── Canje + Dependientes ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-gray-200 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Costo de canjes (S/) — mes a mes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={vista} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => fmt(v)} />
                <Tooltip content={<ChartTooltip soles />} />
                <Bar dataKey="canje" name="Canje" fill={PURPLE} radius={[4, 4, 0, 0]} opacity={0.85}>
                  <LabelList dataKey="canje" position="top" formatter={(v: number) => fmt(v)} style={{ fontSize: 10, fill: '#6b7280' }} />
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Dependientes — registros nuevos y acumulado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={vista} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="bar" tick={{ fontSize: 11 }} width={55} tickFormatter={(v) => fmt(v)} />
                <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => fmt(v)} />
                <Tooltip content={<ChartTooltip />} />
                <Bar yAxisId="bar" dataKey="registros_nuevos" name="Nuevos" fill="#8d82bc" radius={[4, 4, 0, 0]} opacity={0.85} />
                <Line yAxisId="line" dataKey="deps_acum" name="Acumulado" stroke={GREEN} strokeWidth={2} dot={{ r: 3, fill: GREEN }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* ── Envíos WhatsApp mes a mes ── */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Envíos WhatsApp y costo — mes a mes 2027
            </CardTitle>
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Costo por envío</span>
              <span className="text-sm text-muted-foreground">S/</span>
              <input
                type="number" min={0} step={0.01} value={costoEnvio}
                onChange={(e) => setCostoEnvio(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-20 text-right text-lg font-bold bg-transparent border-b border-transparent hover:border-gray-300 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                style={{ color: PURPLE }}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {num(campTotal)} campañas a toda la base + {num(campSeg)} segmentadas al {num(pctSeg)}% ⇒{' '}
            {(num(campTotal) + num(campSeg) * num(pctSeg) / 100).toFixed(2)} envíos por dependiente/mes · se escala con la base acumulada.
          </p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={vista} margin={{ top: 16, right: 24, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="bar" tick={{ fontSize: 11 }} width={65} tickFormatter={(v) => fmt(v)} />
              <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => fmt(v)} />
              <Tooltip content={<ChartTooltip />} />
              <Bar yAxisId="bar" dataKey="envios" name="Envíos" fill={PURPLE} radius={[4, 4, 0, 0]} opacity={0.85}>
                <LabelList dataKey="envios" position="top" formatter={(v: number) => fmt(v)} style={{ fontSize: 10, fill: '#6b7280' }} />
              </Bar>
              <Line yAxisId="line" dataKey="costo_envios" name="Costo S/" stroke={GREEN} strokeWidth={2} dot={{ r: 3, fill: GREEN }} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3 text-center" style={{ backgroundImage: `linear-gradient(to right, ${PURPLE_LIGHT}, transparent 60%)` }}>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Total envíos 2027</p>
              <p className="text-2xl font-bold text-foreground">{fmt(totEnvios)}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3 text-center" style={{ backgroundImage: `linear-gradient(to right, ${PURPLE_LIGHT}, transparent 60%)` }}>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Costo total WhatsApp 2027</p>
              <p className="text-2xl font-bold text-foreground">S/ {fmt(totCostoEnvios)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="text-center text-xs text-muted-foreground/40 pb-2">
        Tonal Strategies · Spike de calibración — proyección 2027 Medifarma Perú
      </p>
    </div>
  )
}
